import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { WorkflowService, DataService } from 'src/app/services';
import { ToastrService } from 'ngx-toastr';
import { InspectionDocument } from 'src/app/models/inspection-document';

@Component({
  selector: 'app-inspections-janus-view-details',
  standalone: false,
  templateUrl: './inspections-janus-view-details.component.html',
  styleUrls: ['./inspections-janus-view-details.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class InspectionsJanusViewDetailsComponent implements OnInit, OnChanges {

  private _row: any;
  private lastFetchedId: string | null = null;

  @Input()
  set row(value: any) {
    const changed = this._row && this._row.id === value?.id && this._row !== value;
    this._row = value;
    if (changed) {
      this.tryFetch(true);
    } else {
      this.tryFetch();
    }
  }

  get row() {
    return this._row;
  }

  // Robust dynamic getter that catches all API casing variations and ensures we never pass 'undefined' to the summary component
  get resolvedAppId(): string {
    return this.resolveApplicationId(this.inspectionDetails, this.row, this.currentActivity);
  }

  @Input() closeExpansion!: () => void;
  @Input() initialTab: 'documents' | 'schedule' | 'complete' | 'notes' | 'LinktoApplication' | 'view-summary' = 'documents';

  @Input() activity: any;

  @Input() onScheduleSubmit!: (data: any) => void;
  @Input() onCompleteSubmit!: (data: any) => void;

  activeTab: string = '';
  currentActivity: any = null;

  tabs = [
    { id: 'documents', label: 'Documents' },
    { id: 'schedule', label: 'Schedule Inspection' },
    { id: 'complete', label: 'Complete Inspection' },
    { id: 'notes', label: 'Notes' },
    { id: 'LinktoApplication', label: 'Link to Application' },
    { id: 'view-summary', label: 'View Summary' }
  ];

  inspectionDetails: any = null;
  inspectionDocuments: InspectionDocument[] = [];
  isLoadingDetails: boolean = false;
  isActivityLoading: boolean = false;
  // Loaded activities keyed by action type; cleared whenever the inspection is (re)loaded or submitted.
  private activityCache = new Map<string, any>();
  // Schedule/complete tab requested before the inspection details were loaded.
  private pendingActivityTab: string | null = null;

  constructor(
    private _workflowSvc: WorkflowService,
    private _dataSvc: DataService,
    private _toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {}

  handleScheduleSubmit(data: any) {
    this.activityCache.clear();
    this.isActivityLoading = true;
    this.cdr.detectChanges();
    if (this.onScheduleSubmit) {
      this.onScheduleSubmit(data);
    }
  }

  handleCompleteSubmit(data: any) {
    this.activityCache.clear();
    this.isActivityLoading = true;
    this.cdr.detectChanges();
    if (this.onCompleteSubmit) {
      this.onCompleteSubmit(data);
    }
  }

  private getValidId(...ids: any[]): string {
    for (const id of ids) {
      if (id === undefined || id === null) continue;
      const safeId = String(id).trim();
      if (safeId && safeId !== 'undefined' && safeId !== 'null') {
        return safeId;
      }
    }
    return '';
  }

  private resolveApplicationId(details?: any, row?: any, activity?: any): string {
    return this.getValidId(
      details?.inspectionApplicationId,
      details?.InspectionApplicationId,
      details?.linkedApplicationId,
      details?.LinkedApplicationId,
      details?.applicationId,
      details?.ApplicationId,
      row?.workflowApplicationId,
      row?.inspectionApplicationId,
      activity?.workflowApplicationId,
      activity?.WorkflowApplicationId,
      activity?.model?.inspection?.workflowApplicationId,
      activity?.model?.inspection?.inspectionApplicationId,
      activity?.model?.scheduleStepActivity?.workflowApplicationId,
      activity?.model?.completeStepActivity?.workflowApplicationId
    );
  }

  private setActivityApplicationId(activity: any, appId: string): any {
    if (!activity || !appId) return activity;
    activity.workflowApplicationId = activity.workflowApplicationId || appId;
    if (activity.model?.inspection) {
      activity.model.inspection.workflowApplicationId = activity.model.inspection.workflowApplicationId || appId;
      activity.model.inspection.inspectionApplicationId = activity.model.inspection.inspectionApplicationId || appId;
    }
    if (activity.model?.scheduleStepActivity) {
      activity.model.scheduleStepActivity.workflowApplicationId = activity.model.scheduleStepActivity.workflowApplicationId || appId;
    }
    if (activity.model?.completeStepActivity) {
      activity.model.completeStepActivity.workflowApplicationId = activity.model.completeStepActivity.workflowApplicationId || appId;
    }
    return activity;
  }

  ngOnInit(): void {
    this.currentActivity = this.activity;
    const appId = this.resolveApplicationId(this.inspectionDetails, this.row, this.currentActivity);
    if (appId) {
      this.currentActivity = this.setActivityApplicationId(this.currentActivity, appId);
    }

    if (this.initialTab === 'schedule' || this.initialTab === 'complete') {
      this.pendingActivityTab = this.initialTab;
    } else if (this.initialTab) {
      setTimeout(() => {
        this.activeTab = this.initialTab;
        this.cdr.detectChanges();
      });
    }

    this.tryFetch();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activity'] && changes['activity'].currentValue) {
      this.currentActivity = changes['activity'].currentValue;
      const appId = this.resolveApplicationId(this.inspectionDetails, this.row, this.currentActivity);
      if (appId) {
        this.currentActivity = this.setActivityApplicationId(this.currentActivity, appId);
      }
    }
    if (changes['initialTab'] && changes['initialTab'].currentValue) {
      this.activeTab = changes['initialTab'].currentValue;
    }
  }

  // Strictly controlled by Server API response flags
  get visibleTabs() {
    return this.tabs.filter(tab => {
      if (tab.id === 'schedule') {
        return this.inspectionDetails?.canScheduleInspection === true;
      }
      if (tab.id === 'complete') {
        return this.inspectionDetails?.canCompleteInspection === true;
      }
      return true;
    });
  }

  private tryFetch(forceRefresh: boolean = false): void {
    const id = this.row?.id;
    if (!id) return;
    if (!forceRefresh && this.lastFetchedId === id) return;
    this.lastFetchedId = id;
    this.fetchInspectionDetails(id);
  }

  setTab(tab: string) {
    if (this.activeTab === tab) return;

    if (tab === 'view-summary') {
       this.activeTab = tab;
       return;
    }

    if (tab === 'schedule' || tab === 'complete') {
       const actionType = tab === 'schedule' ? 'scheduleInspection' : 'completeInspection';

       // Server-driven fallback check
       if (tab === 'schedule' && this.inspectionDetails?.canScheduleInspection !== true) {
          this._toastr.warning('Scheduling is disabled for this inspection.');
          return;
       }
       if (tab === 'complete' && this.inspectionDetails?.canCompleteInspection !== true) {
          this._toastr.warning('Completing is disabled for this inspection.');
          return;
       }

       const cachedActivity = this.activityCache.get(actionType);
       if (cachedActivity) {
         this.currentActivity = cachedActivity;
         this.activeTab = tab;
         return;
       }

       const appId = this.resolveApplicationId(this.inspectionDetails, this.row, this.currentActivity);
       if (!appId) {
         this._toastr.error('Application ID is missing, cannot load activity.');
         this.activeTab = 'documents';
         return;
       }

       this.activeTab = '';
       this.isActivityLoading = true;
       this.cdr.detectChanges();

       this._workflowSvc.getInspectionActivity(appId, this.row.id, actionType).subscribe({
          next: (res) => {
             if (res) {
                this.currentActivity = this.setActivityApplicationId(res, appId);
                this.activityCache.set(actionType, this.currentActivity);
                this.extractScheduledDateFromActivity(res);
                this.extractRequestedDateFromActivity(res);
                if (tab === 'complete') {
                  this.extractCompletedDataFromActivity(res);
                }
                this.activeTab = tab;
              } else {
                this._toastr.info('The action you requested is not available for this inspection.', 'Action unavailable.');
                this.activeTab = 'documents';
              }
              this.isActivityLoading = false;
              this.cdr.detectChanges();
          },
          error: (err) => {
             this._toastr.error('Failed to load activity details.');
             this.activeTab = 'documents';
             this.isActivityLoading = false;
             this.cdr.detectChanges();
          }
       });
    }
    else {
       this.activeTab = tab;
    }
  }

  getTabButtonData(tab: any) {
    return {
      text: tab.label,
      onClick: () => this.setTab(tab.id)
    };
  }

  fetchInspectionDetails(inspectionId: string) {
    this.activityCache.clear();
    this.isLoadingDetails = true;

    this._workflowSvc.getInspectionJanus(inspectionId).subscribe({
      next: (res) => {
        this.inspectionDetails = res;
        this.inspectionDocuments = res?.documents || [];
        this.isActivityLoading = false;

        const requestedTab = this.pendingActivityTab || this.activeTab;
        this.pendingActivityTab = null;
        if (requestedTab === 'schedule' || requestedTab === 'complete') {
          // Reload the activity fresh; after scheduling, move on to Complete when it becomes available.
          this.activeTab = '';
          if (this.visibleTabs.some(t => t.id === requestedTab)) {
            this.setTab(requestedTab);
          } else if (requestedTab === 'schedule' && this.inspectionDetails?.canCompleteInspection) {
            this.setTab('complete');
          } else {
            this.activeTab = 'documents';
          }
        } else if (!this.visibleTabs.some(t => t.id === this.activeTab)) {
          this.activeTab = 'documents';
        }

        const appId = this.resolveApplicationId(res, this.row, this.currentActivity);
        if (appId) {
          this.currentActivity = this.setActivityApplicationId(this.currentActivity, appId);
        }

        this.isLoadingDetails = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this._toastr.error('Failed to load inspection details');
        this.isLoadingDetails = false;
        this.cdr.detectChanges();
      }
    });
  }

  // Extracts the specific submitted Date/Time from the Dynamic Schedule Form Data
  private extractScheduledDateFromActivity(activityRes: any) {
    if (!this.inspectionDetails || !activityRes) return;

    // Check root inspection object first
    let isoDateStr = activityRes.scheduledOn || activityRes.model?.inspection?.scheduledOn;

    // Dig into the dynamic form JSON if not at the root
    let layoutModel = activityRes.model?.formLayoutModel;
    if (activityRes.type === 'consolidated-inspection-activity') {
      layoutModel = activityRes.model?.scheduleStepActivity?.model?.formLayoutModel;
    }

    if (layoutModel?.columnEntities) {
      const dateEntity = layoutModel.columnEntities.find((c: any) =>
         c.entity?.templateCode === 'SCHEDULE_SDL_USER' ||
         (c.entity?.dataEntityTypeCode === 'date' && c.entity?.label?.toLowerCase().includes('sched'))
      )?.entity;

      if (dateEntity && dateEntity.value) {
        try {
          const parsed = JSON.parse(dateEntity.value);
          if (parsed?.Value && !parsed.Value.includes('0001-01-01')) {
            isoDateStr = parsed.Value;
          }
        } catch (e) {
          isoDateStr = dateEntity.value;
        }
      }
    }

    if (isoDateStr) {
       this.inspectionDetails.activityScheduledOn = isoDateStr;
    }
  }

  private extractCompletedDataFromActivity(activityRes: any) {
    if (!this.inspectionDetails || !activityRes) return;

    let isoDateStr = activityRes.completedOn || activityRes.model?.inspection?.completedOn;
    let resultText = '';
    let notesText = activityRes.notes || activityRes.model?.inspection?.notes || '';

    let layoutModel = activityRes.model?.formLayoutModel;
    if (activityRes.type === 'consolidated-inspection-activity') {
      layoutModel = activityRes.model?.completeStepActivity?.model?.formLayoutModel || layoutModel;
    }

    if (layoutModel?.columnEntities) {
      const completedDateEntity = layoutModel.columnEntities.find((c: any) =>
        c.entity?.templateCode === 'COMPLETE_DATE' ||
        (c.entity?.dataEntityTypeCode === 'date' && c.entity?.label?.toLowerCase().includes('complete'))
      )?.entity;

      const resultEntity = layoutModel.columnEntities.find((c: any) =>
        c.entity?.templateCode === 'COMPLETE_APPROVE' ||
        c.entity?.templateCode === 'COMPLETE_RESULT' ||
        (c.entity?.dataEntityTypeCode === 'list-data' && c.entity?.label?.toLowerCase().includes('result')) ||
        (c.entity?.dataEntityTypeCode === 'list-data' && c.entity?.label?.toLowerCase().includes('approve'))
      )?.entity;

      const notesEntity = layoutModel.columnEntities.find((c: any) =>
        c.entity?.templateCode === 'COMPLETE_SDL_NOTES' ||
        c.entity?.label?.toLowerCase().includes('note')
      )?.entity;

      if (completedDateEntity?.value) {
        try {
          const parsed = JSON.parse(completedDateEntity.value);
          if (parsed?.Value && !parsed.Value.includes('0001-01-01')) {
            isoDateStr = parsed.Value;
          }
        } catch (e) {
          isoDateStr = completedDateEntity.value;
        }
      }

      if (resultEntity) {
        const selectedValue = resultEntity.selectedValue || resultEntity.value || '';
        const rawOptions = resultEntity.listTextValues?.length ? resultEntity.listTextValues : (resultEntity.availableListItems || []);
        const selectedOption = rawOptions.find((o: any) => String(o.value) === String(selectedValue));
        resultText = selectedOption?.text || selectedValue;
      }

      if (notesEntity?.value) {
        notesText = notesEntity.value;
      }
    }

    if (isoDateStr && !String(isoDateStr).includes('0001-01-01')) {
      this.inspectionDetails.activityCompletedOn = isoDateStr;
    }
    if (resultText) {
      this.inspectionDetails.activityCompletionResult = resultText;
    }
    if (notesText) {
      this.inspectionDetails.activityCompletionNotes = notesText;
    }
  }

  // Extracts the specific submitted Date/Time from the Dynamic Request Form Data
  private extractRequestedDateFromActivity(activityRes: any) {
    if (!this.inspectionDetails || !activityRes) return;

    let isoDateStr = activityRes.model?.inspection?.requestedDate || activityRes.model?.inspection?.requestedOn || activityRes.requestedDate || activityRes.requestedOn;
    let hasTime = false;
    let amPmPref = '';

    let layoutModel = activityRes.model?.formLayoutModel;
    if (activityRes.type === 'consolidated-inspection-activity') {
      layoutModel = activityRes.model?.requestStepActivity?.model?.formLayoutModel;
    }

    if (layoutModel?.columnEntities) {
      const dateEntity = layoutModel.columnEntities.find((c: any) =>
          c.entity?.templateCode === 'REQUEST_SDL_USER' ||
         (c.entity?.dataEntityTypeCode === 'date' && c.entity?.label?.toLowerCase().includes('request'))
      )?.entity;

      const amPmEntity = layoutModel.columnEntities.find((c: any) =>
          c.entity?.dataEntityTypeCode === 'list-data' &&
          (c.entity?.label?.toLowerCase().includes('am/pm') || c.entity?.label?.toLowerCase().includes('time'))
      )?.entity;

      if (amPmEntity) {
          amPmPref = amPmEntity.formattedValue || '';

          if (!amPmPref) {
              const raw = amPmEntity.selectedValue || amPmEntity.value || '';

              try {
                  const parsed = JSON.parse(raw);
                  amPmPref = Array.isArray(parsed)
                      ? parsed[0]?.Text || parsed[0]?.Value || ''
                      : raw;
              } catch {
                  amPmPref = raw;
              }
          }

      }



      if (dateEntity && dateEntity.value) {
        try {
          const parsed = JSON.parse(dateEntity.value);
          if (parsed?.Value && !parsed.Value.includes('0001-01-01')) {
            isoDateStr = parsed.Value;
            hasTime = parsed.HasTime === true;
          }
        } catch (e) {
          if (dateEntity.value && !dateEntity.value.includes('0001-01-01')) {
            isoDateStr = dateEntity.value;
          }
        }
      }
    }

    if (isoDateStr && !isoDateStr.includes('0001-01-01')) {
       // Fallback check: if it wasn't parsed from JSON but it's exactly midnight UTC, it has no time.
       if (!hasTime && isoDateStr.includes('T00:00:00')) hasTime = false;

       this.inspectionDetails.activityRequestedOn = isoDateStr;
       this.inspectionDetails.activityRequestedHasTime = hasTime;
       this.inspectionDetails.activityRequestedAmPm = amPmPref;
    }
  }

  onInspectionDocsAdded(documents: InspectionDocument[]) {
    if (!documents || documents.length === 0 || !this.row?.id) return;

    this._dataSvc.saveInspectionDocuments(this.row.id, documents).subscribe({
      next: docs => {
        this.inspectionDocuments = docs || [];
        this._toastr.success('Document saved successfully.');
      },
      error: () => {
        this._toastr.error('Failed to save document.');
      }
    });
  }

  onInspectionDocDeleted(id: string) {
    if (!id) return;

    this._dataSvc.deleteInspectionDocument(id).subscribe({
      next: () => {
        this.inspectionDocuments = this.inspectionDocuments.filter(d => d.id !== id);
        this._toastr.success('Document deleted successfully.');
      },
      error: () => {
        this._toastr.error('Failed to delete document.');
      }
    });
  }

  onInspectionDocSaved(doc: InspectionDocument) {
    if (!doc) return;

    this._dataSvc.saveInspectionDocument(doc).subscribe({
      next: savedDoc => {
        const idx = this.inspectionDocuments.findIndex(d => d.id === savedDoc?.id);
        if (idx > -1) {
          this.inspectionDocuments[idx] = savedDoc;
          this.inspectionDocuments = [...this.inspectionDocuments];
        }
        this._toastr.success('Document updated successfully.');
      },
      error: () => {
        this._toastr.error('Failed to update document.');
      }
    });
  }
  // Added by Vipul on 30-09-2026
  getVisibleTabs(): any[] {
    return this.visibleTabs.filter(
      tab => tab.id !== 'LinktoApplication' || this.inspectionDetails?.noSchedule
    );
  }

  getActiveTabIndex(): number {
    const tabs = this.getVisibleTabs();

    return tabs.findIndex(tab => tab.id === this.activeTab);
  }

  onTabIndexChange(index: number): void {
    const tabs = this.getVisibleTabs();

    if (tabs[index]) {
      this.activeTab = tabs[index].id;
    }
  }
  // End by Vipul on 30-09-2026
}
